import mqtt from 'mqtt';
import { appConfig } from '@common/config/app.config';
import { EventMessageData, UpdateEventMessageData } from './types';
import { ConnectedSocket, setAllClients } from '@common/reducers/ConnectedEnginesSlice';
import { getStore } from '@common/state/state-provider';

/**
 * MQTT Client Events Implementation:
 * 
 * CHANGED: Replaced system topic subscriptions with webhook-based client tracking:
 * - OLD: Subscribed to '$SYS/brokers/+/clients/+/connected' and '$SYS/brokers/+/clients/+/disconnected'
 * - NEW: EMQX webhooks publish client events to 'users/{userId}/downstream/client-events'
 * 
 * Backend Implementation:
 * - EMQX configured with webhooks that call backend '/api/v1/emqx/webhook'
 * - Backend tracks clients in Redis with 6-hour TTL: user/{userId}/client/{clientId}
 * - Backend publishes complete clients list on connect/disconnect events only
 * 
 * Message format: {
 *   event: 'connected' | 'disconnected',
 *   clientId: string,
 *   timestamp: number,
 *   connectedClients: ConnectedSocket[] // Complete current clients list
 * }
 * 
 * Benefits:
 * - No processing of ALL broker client events (scalable)
 * - Automatic Redis TTL cleanup for stale connections (6-hour expiry)
 * - Complete state updates on each change (no sync issues)
 * - Backward compatible API (returns empty array)
 * - Simple connect/disconnect tracking only
 */

export interface MQTTConfig {
  brokerUrl: string;
  username: string;
  password: string;
  userId?: string;
}

export class MQTTSubscriber {
  private static instance: MQTTSubscriber;
  private client: mqtt.MqttClient | null = null;
  private config: MQTTConfig | null = null;
  private userId: string | null = null;
  private currentClientId: string | null = null;
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private subscribedTopics: Set<string> = new Set();
  private isConnecting: boolean = false;
  private debounceTimer: NodeJS.Timeout | null = null;

  private constructor() { }

  public static getInstance(): MQTTSubscriber {
    if (!MQTTSubscriber.instance) {
      MQTTSubscriber.instance = new MQTTSubscriber();
    }
    return MQTTSubscriber.instance;
  }

  public connect(userId: string, accessToken: string): void {
    // Check client's actual state - more robust than manual flags
    if (this.client && (this.client.connected || this.client.reconnecting || this.client.disconnecting)) {
      console.log('[MQTT] Already connected, reconnecting, or disconnecting. Skipping connection. State:', {
        connected: this.client.connected,
        reconnecting: this.client.reconnecting,
        disconnecting: this.client.disconnecting
      });
      return;
    }

    // Check if already attempting to connect
    if (this.isConnecting) {
      console.log('[MQTT] Connection attempt already in progress, skipping duplicate connection');
      return;
    }

    // Clean up any existing client and its listeners
    if (this.client) {
      console.log('[MQTT] Cleaning up existing client before creating new connection');
      this.cleanupClient();
    }

    console.log('[MQTT] Connecting to broker...');
    this.isConnecting = true;

    const mqttBrokerUrl = appConfig.MQTT_BROKER_URL;
    console.log('[MQTT] Connecting to broker...', mqttBrokerUrl)
    this.userId = userId;
    this.config = {
      brokerUrl: appConfig.MQTT_BROKER_URL || "wss://mqtt.blueticks.co:443/mqtt",
      username: userId, // userId as username
      password: accessToken, // JWT token as password
      userId
    };

    // Determine client type based on localStorage sessionId and domain
    const hasSessionId = typeof localStorage !== 'undefined' && localStorage.getItem('sessionId');
    const isWhatsAppDomain = typeof window !== 'undefined' && window.location.hostname === 'web.whatsapp.com';

    let clientType: string;
    if (hasSessionId) {
      clientType = 'gateway';
    } else if (isWhatsAppDomain) {
      clientType = 'whatsapp';
    } else {
      clientType = 'frontend';
    }

    const generatedClientId = `${clientType}_${userId}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    this.currentClientId = generatedClientId;

    try {
      this.client = mqtt.connect(this.config.brokerUrl, {
        clientId: this.currentClientId,
        username: this.config.username,
        password: this.config.password,
        clean: true,
        reconnectPeriod: 5000,
        connectTimeout: 60000, // Increased from 30000 to 60000 (60 seconds)
        keepalive: 60,
        rejectUnauthorized: false,
        protocol: 'wss',
        // Add additional stability options
        reschedulePings: true,
        protocolVersion: 4, // Use MQTT 3.1.1
        // Disable Web Workers to avoid CSP issues on WhatsApp domain
        manualConnect: false,
        timerVariant: 'native', // Use native timers instead of Web Workers
        properties: {
          maximumPacketSize: 65535
        }
      });

      this.setupEventHandlers();
    } catch (error) {
      console.error('[MQTT] Connection error:', error);
      // Reset client and connection state on error
      this.client = null;
      this.currentClientId = null;
      this.isConnecting = false;
    }
  }

  public disconnect(): void {
    if (this.client) {
      console.log('[MQTT] Disconnecting client:', this.currentClientId);
      this.cleanupClient();
    }

    this.client = null;
    this.currentClientId = null;
    this.isConnecting = false; // Reset connection flag
  }

  private cleanupClient(): void {
    if (!this.client) return;

    console.log('[MQTT] Cleaning up client listeners and connection');

    // Remove all event listeners to prevent memory leaks
    this.client.removeAllListeners();

    // Force disconnect
    this.client.end(true);

    // Clear subscription tracking
    this.subscribedTopics.clear();

    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.isConnecting = false; // Reset connection flag
  }

  public onMessage(eventType: string, handler: (data: any) => void): void {
    this.messageHandlers.set(eventType, handler);
  }

  private setupEventHandlers(): void {
    if (!this.client) return;

    console.log('[MQTT] Setting up event handlers');

    this.client.on('connect', () => {
      console.log('[MQTT] Connected to broker. Client ID:', this.currentClientId);
      this.isConnecting = false; // Reset connection flag on success

      this.subscribeToTopics();
      // Client list will be updated via webhook events when other clients connect/disconnect
    });

    this.client.on('disconnect', (packet) => {
      console.log('[MQTT] Disconnected from broker. Packet:', packet);
    });

    this.client.on('error', (error) => {
      console.error('[MQTT] Connection error:', error);
      this.isConnecting = false; // Reset connection flag on error

      // Handle specific error types
      if (error.message && error.message.includes('connack timeout')) {
        console.error('[MQTT] Connection acknowledgment timeout - broker may be overloaded or unreachable');
      } else if (error.message && error.message.includes('Connection refused')) {
        console.error('[MQTT] Connection refused - check credentials or broker availability');
      }
    });

    this.client.on('offline', () => {
      console.warn('[MQTT] Client is offline');
    });

    this.client.on('reconnect', () => {
      console.log('[MQTT] Attempting to reconnect...');
    });

    this.client.on('close', () => {
      console.log('[MQTT] Connection closed');
    });

    this.client.on('message', (topic, message) => {
      try {
        const messageString = message.toString();
        let messageData: any;
        if (messageString === "") {
          messageData = { status: 'disconnected' };
        } else {
          messageData = JSON.parse(messageString);
        }

        // Handle regular application messages (including client events)
        this.handleMessage(topic, messageData);
      } catch (error) {
        console.error('[MQTT] Error parsing or handling message:', error, "Topic:", topic, "Message:", message.toString());
      }
    });
  }

  private subscribeToTopics(): void {
    if (!this.client || !this.userId) return;

    const userTopics = [
      `users/${this.userId}/downstream/+`,  // General user updates (including client-events)
      `users/${this.userId}/requests/+`, // Request topics for request-response pattern
    ];

    userTopics.forEach(topic => {
      this.client?.subscribe(topic, (err) => {
        if (err) {
          console.error(`[MQTT] Failed to subscribe to ${topic}:`, err);
        } else {
          console.log(`[MQTT] Subscribed to ${topic}`);
          this.subscribedTopics.add(topic);
        }
      });
    });
  }

  private handleMessage(topic: string, messageData: any): void {
    console.log(`[MQTT] Received message on topic ${topic}:`, messageData);

    // Handle request-response pattern
    if (topic.includes('/requests/')) {
      this.handleRequest(topic, messageData);
      return;
    }

    // Handle client connection events
    if (topic.endsWith('/client-events')) {
      this.handleClientEvents(topic, messageData);
      return;
    }

    // Map MQTT topic to Socket.IO event type for regular updates
    const eventType = this.extractEventTypeFromTopic(topic);

    if (eventType) {
      console.log(`[MQTT] Mapped topic ${topic} to event type: ${eventType}`);

      // Convert MQTT message to Socket.IO format
      const socketIOMessage = this.convertToSocketIOFormat(messageData, eventType);

      // Trigger the appropriate handler
      const handler = this.messageHandlers.get(eventType);
      if (handler) {
        console.log(`[MQTT] Dispatching ${eventType} event to handler`);
        handler(socketIOMessage);
      } else {
        console.warn(`[MQTT] No handler found for event type: ${eventType}`);
      }
    } else {
      console.warn(`[MQTT] Could not extract event type from topic: ${topic}`);
    }
  }

  private handleRequest(topic: string, requestData: any): void {
    console.log(`[MQTT] Handling request on topic ${topic}:`, requestData);

    // Extract requestId from topic: users/{userId}/requests/{requestId}
    const parts = topic.split('/');
    const requestId = parts[3];

    if (!requestId || !requestData.command) {
      console.error(`[MQTT] Invalid request format:`, { requestId, command: requestData.command });
      return;
    }

    // Handle the request and send response
    this.processRequest(requestId, requestData.command, requestData.params || {});
  }

  private async processRequest(requestId: string, command: string, params: any): Promise<void> {
    try {
      console.log(`[MQTT] Processing request ${requestId}: ${command}`);

      // Create a callback function that publishes the response
      const responseCallback = (response: any) => {
        this.publishResponse(requestId, response);
      };

      // Handle insertOnlyIfMissing requests
      if (command === 'insertOnlyIfMissing') {
        const requestEvent = {
          path: 'insertOnlyIfMissing',
          method: command,
          entity: params
        };
        await this.handleInsertOnlyIfMissingRequest(requestEvent, responseCallback);
        return;
      }

      // Create the request event in the same format as the Socket.IO version for whatsapp-interface
      const requestEvent = {
        path: 'whatsapp-interface',
        method: command,
        entity: {
          params,
          owner: this.userId
        }
      };

      // Handle WhatsApp interface requests directly
      await this.handleWhatsappInterfaceRequest(requestEvent, responseCallback);
    } catch (error) {
      console.error(`[MQTT] Error processing request ${requestId}:`, error);
      this.publishResponse(requestId, {
        success: false,
        error: error.message || 'Unknown error'
      });
    }
  }

  private async handleWhatsappInterfaceRequest(requestEvent: any, callback: (response: any) => void): Promise<void> {
    // Import the handler dynamically to avoid circular dependencies
    try {
      const { handleWhatsappInterfaceEvents } = await import('./NotiferHandlers');
      await handleWhatsappInterfaceEvents(requestEvent, callback);
    } catch (error) {
      console.error(`[MQTT] Error importing WhatsApp interface handler:`, error);
      callback({
        success: false,
        error: 'Failed to load WhatsApp interface handler'
      });
    }
  }

  private async handleInsertOnlyIfMissingRequest(requestEvent: any, callback: (response: any) => void): Promise<void> {
    // Import the handler dynamically to avoid circular dependencies
    try {
      const { handleInsertOnlyIfMissingRequest } = await import('./NotiferHandlers');
      await handleInsertOnlyIfMissingRequest(requestEvent, callback);
    } catch (error) {
      console.error(`[MQTT] Error importing insertOnlyIfMissing handler:`, error);
      callback({
        success: false,
        error: 'Failed to load insertOnlyIfMissing handler'
      });
    }
  }

  private publishResponse(requestId: string, response: any): void {
    if (!this.client) {
      console.error(`[MQTT] Cannot publish response - client not connected`);
      return;
    }

    // Publish to the user's upstream topic which is allowed by ACL rules
    // Format: users/{userId}/upstream/responses/{requestId}
    const responseTopic = `users/${this.userId}/upstream/responses/${requestId}`;
    const responseMessage = {
      requestId,
      success: response.success !== false, // Default to true unless explicitly false
      data: response.success !== false ? response.data : undefined, // Extract just the data field
      error: response.success === false ? response.error : undefined,
      _timestamp: Date.now()
    };

    const message = JSON.stringify(responseMessage);

    this.client.publish(responseTopic, message, { qos: 1 }, (error) => {
      if (error) {
        console.error(`[MQTT] Failed to publish response to ${responseTopic}:`, error);
      } else {
        console.log(`[MQTT] Published response for request ${requestId} to ${responseTopic}`);
      }
    });
  }

  private extractEventTypeFromTopic(topic: string): string | null {
    // Extract event type from topic patterns
    // users/{userId}/downstream/{path} -> "update" for entity updates, specific types for system events
    // users/{userId}/downstream/boards/{boardId} -> "update" (board update)
    // users/{userId}/downstream/workspaces/{workspaceId} -> "update" (workspace update)
    // users/{userId}/downstream/client-events -> "client-events" (client connection events)
    // boards/{boardId}/downstream -> "update" (direct board update)
    // workspaces/{workspaceId}/downstream -> "update" (direct workspace update)

    const parts = topic.split('/');

    // Handle direct board and workspace topics
    if (parts[0] === 'boards' && parts[2] === 'downstream') {
      return 'update'; // Direct board update
    }

    if (parts[0] === 'workspaces' && parts[2] === 'downstream') {
      return 'update'; // Direct workspace update
    }

    // Handle user-specific topics
    if (parts[0] === 'users' && parts[2] === 'downstream') {
      if (parts[3] === 'boards') {
        return 'update'; // Board update
      } else if (parts[3] === 'workspaces') {
        return 'update'; // Workspace update  
      } else if (parts[3] === 'client-events') {
        return 'client-events'; // Client connection events
      } else {
        // Check if this is a special system event
        const eventType = parts[3];
        if (['sync-user-data', 'chatbot-progress', 'document-processing-progress'].includes(eventType)) {
          return eventType; // Special system events
        } else {
          return 'update'; // All other entity updates (tasks, notifications, etc.)
        }
      }
    }

    if (parts[0] === 'admin' && parts[1] === 'dashboard' && parts[2] === 'downstream') {
      return 'update'; // Admin dashboard events should also be "update"
    }

    return null;
  }

  private convertToSocketIOFormat(messageData: any, eventType: string): any {
    // Convert MQTT message format to match Socket.IO event expectations
    // The backend publishes the same data structure, so this should be a pass-through
    return messageData;
  }

  public isConnected(): boolean {
    return this.client ? this.client.connected : false;
  }

  private async queryConnectedClients(): Promise<void> {
    try {
      // Call our backend API using RTK Query mutation
      const { getConnectedClients } = await import('@common-reducers/backend-api/backend-api');

      const store = getStore();
      const dispatch = store?.dispatch;

      if (!dispatch) {
        console.error('[MQTT] Store dispatch is not available for querying connected clients');
        return;
      }

      // Dispatch the RTK Query mutation and unwrap the result
      const connectedSockets: ConnectedSocket[] = await (dispatch as any)(getConnectedClients.initiate()).unwrap();

      dispatch(setAllClients(connectedSockets));
      console.log(`[MQTT] Updated client list from backend API:`, connectedSockets);

    } catch (error) {
      console.error('[MQTT] Failed to query connected clients from backend:', error);
    }
  }

  private debouncedQueryConnectedClients(): void {
    // Clear existing timer if it exists
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Set a new timer for 1 second
    this.debounceTimer = setTimeout(() => {
      console.log('[MQTT] Debounced refresh triggered - querying connected clients');
      this.queryConnectedClients();
      this.debounceTimer = null;
    }, 1000);
  }

  /**
   * Generic method to subscribe to a topic with automatic cleanup of similar topics
   * @param topicPattern - The topic to subscribe to
   * @param entityType - Optional entity type for grouping (e.g., 'boards', 'workspaces')
   */
  public subscribeToTopic(topicPattern: string, entityType?: string): void {
    if (!this.client || !this.userId) return;

    // If entityType is provided, find and unsubscribe from existing topics of the same type
    if (entityType) {
      const existingTopic = Array.from(this.subscribedTopics).find(topic =>
        topic.includes(`/${entityType}/`) && topic !== topicPattern
      );

      if (existingTopic) {
        this.client.unsubscribe(existingTopic, (err) => {
          if (err) {
            console.error(`[MQTT] Failed to unsubscribe from previous ${entityType} topic:`, err);
          } else {
            console.log(`[MQTT] Unsubscribed from previous ${entityType} topic: ${existingTopic}`);
            this.subscribedTopics.delete(existingTopic);
          }
        });
      }
    }

    // Check if already subscribed to this exact topic
    if (this.subscribedTopics.has(topicPattern)) {
      console.log(`[MQTT] Already subscribed to topic: ${topicPattern}`);
      return;
    }

    // Subscribe to new topic
    this.client.subscribe(topicPattern, (err) => {
      if (err) {
        console.error(`[MQTT] Failed to subscribe to topic ${topicPattern}:`, err);
      } else {
        console.log(`[MQTT] Subscribed to topic: ${topicPattern}`);
        this.subscribedTopics.add(topicPattern);
      }
    });
  }

  /**
   * Generic method to unsubscribe from a topic
   */
  public unsubscribeFromTopic(topicPattern: string): void {
    if (!this.client) return;

    if (this.subscribedTopics.has(topicPattern)) {
      this.client.unsubscribe(topicPattern, (err) => {
        if (err) {
          console.error(`[MQTT] Failed to unsubscribe from topic ${topicPattern}:`, err);
        } else {
          console.log(`[MQTT] Unsubscribed from topic: ${topicPattern}`);
          this.subscribedTopics.delete(topicPattern);
        }
      });
    } else {
      console.log(`[MQTT] Not subscribed to topic: ${topicPattern}`);
    }
  }

  /**
   * Convenience method for entity subscriptions (boards, workspaces, etc.)
   */
  public subscribeToEntity(entityType: 'boards' | 'workspaces', entityId: string): void {
    const topicPattern = `${entityType}/${entityId}/downstream`;
    this.subscribeToTopic(topicPattern, entityType);
  }

  /**
   * Get all current subscriptions for debugging
   */
  public getCurrentSubscriptions(): string[] {
    return Array.from(this.subscribedTopics);
  }

  private handleClientEvents(topic: string, messageData: any): void {
    console.log(`[MQTT] Handling client events on topic ${topic}:`, messageData);

    // The messageData now contains the complete connected clients list
    // Expected format: { 
    //   event: 'connected'|'disconnected', 
    //   clientId: string, 
    //   timestamp: number,
    //   connectedClients: ConnectedSocket[]
    // }
    
    if (messageData.event === 'connected' || messageData.event === 'disconnected') {
      console.log(`[MQTT] Client ${messageData.event}: ${messageData.clientId}`);
      
      // Update the Redux store with the complete clients list
      if (messageData.connectedClients && Array.isArray(messageData.connectedClients)) {
        try {
          const store = getStore();
          const dispatch = store?.dispatch;

          if (dispatch) {
            dispatch(setAllClients(messageData.connectedClients));
            console.log(`[MQTT] Updated client list with ${messageData.connectedClients.length} connected clients from webhook event`);
          } else {
            console.error('[MQTT] Store dispatch not available for updating client list');
          }
        } catch (error) {
          console.error('[MQTT] Error updating client list from webhook event:', error);
        }
      } else {
        console.warn('[MQTT] No connectedClients array in webhook event, falling back to API query');
        // Fallback to API query if data is malformed
        this.debouncedQueryConnectedClients();
      }
    } else {
      console.warn(`[MQTT] Unknown client event type:`, messageData.event);
    }
  }
} 